import { ServiceOrderStatus, type ServiceOrderEventDto } from '@bhops/shared';

// Renders a ServiceOrderEvent's `type` + `payload` JSON into a
// human-readable label + summary for the timeline UI.
//
// Each event type knows what shape its payload has (see the
// service-side write paths in apps/api/src/service-orders/
// service-orders.service.ts). When the payload doesn't match the
// expected shape (forward-compat: server-side schema drift), we
// gracefully fall back to the raw type name so the timeline never
// crashes — the unknown event is still visible, just unparsed.
//
// Strings are Korean to match the rest of the windows ops UI.

const STATUS_LABEL: Record<ServiceOrderStatus, string> = {
  OPEN: '신규',
  TRIAGED: '분류완료',
  ASSIGNED: '배정',
  IN_PROGRESS: '진행중',
  BLOCKED: '대기',
  RESOLVED: '해결',
  VERIFIED: '검증완료',
  CLOSED: '종료',
  CANCELLED: '취소',
};

// Subset of ServiceOrderEventType we know how to format. Unknown
// types render the raw type string for forward-compat — adding
// new server-side event types doesn't require a windows build
// before the timeline reads cleanly.
//
// Mirrors the canonical enum in packages/shared/src/enums.ts and
// the matching server-side enum in packages/prisma/prisma/
// schema.prisma. Don't extend in-place — both sources are the
// authoritative drift checkers.
const EVENT_TYPE_LABEL: Record<string, string> = {
  CREATED: '생성',
  STATUS_CHANGED: '상태 변경',
  ASSIGNED: '배정',
  NOTE_ADDED: '메모 추가',
  PARTS_ADDED: '부품 추가',
  PHOTO_ADDED: '사진 추가',
  SLA_BREACHED: 'SLA 초과',
  ESCALATED: '에스컬레이션',
  CANCELLED: '취소',
  CUSTOMER_REASSIGNED: '고객 재배정',
  // round7 §1-5 (D5) — 등급 재분류.
  SEVERITY_CHANGED: '등급 변경',
};

export interface FormattedEvent {
  /** Human-readable Korean type label. e.g. "상태 변경", "고객 재배정". */
  label: string;
  /**
   * One-line payload summary in Korean, rendered below the
   * label. Empty string when there's no useful payload to
   * surface (e.g. an event that's just a marker).
   */
  summary: string;
}

// Helpers
function statusLabel(s: unknown): string {
  if (typeof s === 'string' && s in STATUS_LABEL) return STATUS_LABEL[s as ServiceOrderStatus];
  return String(s ?? '');
}

function shortId(id: unknown, max = 8): string {
  if (typeof id !== 'string') return '';
  if (id.length <= max) return id;
  // cuid ids start with a stable prefix; surface the trailing N
  // chars which are the high-entropy part.
  return `…${id.slice(-max)}`;
}

function truncate(s: unknown, max = 80): string {
  if (typeof s !== 'string') return '';
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}

function formatStatusChanged(payload: Record<string, unknown>): string {
  const from = statusLabel(payload.from);
  const to = statusLabel(payload.to);
  const restored = payload.restored === true ? ' (관리자 복구)' : '';
  const note =
    typeof payload.note === 'string' && payload.note.trim() !== ''
      ? ` — ${truncate(payload.note)}`
      : '';
  return `${from} → ${to}${restored}${note}`;
}

function formatAssigned(payload: Record<string, unknown>): string {
  const assignee = payload.assignedToId;
  if (assignee === null || assignee === undefined || assignee === '') {
    return '미배정으로 변경';
  }
  return `→ ${shortId(assignee)}`;
}

function formatCancelled(payload: Record<string, unknown>): string {
  const from = statusLabel(payload.from);
  const reason = truncate(payload.reason);
  return `${from} → 취소${reason ? `, 사유: ${reason}` : ''}`;
}

function formatCustomerReassigned(payload: Record<string, unknown>): string {
  const fromCustomerId = shortId(payload.fromCustomerId);
  const toCustomerId = shortId(payload.toCustomerId);
  const fromStoreId = payload.fromStoreId;
  const toStoreId = payload.toStoreId;
  const reason = truncate(payload.reason, 60);

  const customerLine = `고객 ${fromCustomerId} → ${toCustomerId}`;
  // Surface a store-change line only when at least one side is
  // non-null AND the pair actually differs. The reassign endpoint
  // CAN no-op on customer if the store changed — preserve both
  // lines so the timeline reads "고객 c1 → c1 / 매장 s1 → null"
  // unambiguously.
  let storeLine = '';
  if (fromStoreId || toStoreId) {
    if (fromStoreId !== toStoreId) {
      storeLine = ` / 매장 ${fromStoreId ? shortId(fromStoreId) : '(없음)'} → ${
        toStoreId ? shortId(toStoreId) : '(없음)'
      }`;
    }
  }
  return `${customerLine}${storeLine}${reason ? ` — ${reason}` : ''}`;
}

function formatSeverityChanged(payload: Record<string, unknown>): string {
  // round7 §1-5 (D5) — from/to 는 ServiceSeverity (P1/P2/P3) 원문.
  const from = typeof payload.from === 'string' ? payload.from : '';
  const to = typeof payload.to === 'string' ? payload.to : '';
  const reason = truncate(payload.reason, 60);
  return `${from} → ${to}${reason ? ` — ${reason}` : ''}`;
}

function formatSlaBreached(payload: Record<string, unknown>): string {
  const deadline = payload.deadlineType;
  const map: Record<string, string> = {
    triage: '분류',
    resolve: '해결',
    verify: '검증',
  };
  const label =
    typeof deadline === 'string' && deadline in map ? map[deadline] : String(deadline ?? '');
  return `${label} 마감 초과`;
}

function formatCreated(payload: Record<string, unknown>): string {
  // Description is the most operator-relevant bit; the rest
  // (category, severity, humanId) live in the SO header already.
  return truncate(payload.description);
}

/**
 * Render an event into `{ label, summary }`. Caller is responsible
 * for layout — typically the label renders bold and the summary
 * renders below it in a muted color.
 */
export function formatServiceOrderEvent(evt: ServiceOrderEventDto): FormattedEvent {
  const label = EVENT_TYPE_LABEL[evt.type] ?? evt.type;
  const payload =
    evt.payload && typeof evt.payload === 'object' && !Array.isArray(evt.payload)
      ? (evt.payload as Record<string, unknown>)
      : {};

  let summary = '';
  switch (evt.type) {
    case 'CREATED':
      summary = formatCreated(payload);
      break;
    case 'STATUS_CHANGED':
      summary = formatStatusChanged(payload);
      break;
    case 'ASSIGNED':
      summary = formatAssigned(payload);
      break;
    case 'CANCELLED':
      summary = formatCancelled(payload);
      break;
    case 'CUSTOMER_REASSIGNED':
      summary = formatCustomerReassigned(payload);
      break;
    case 'SEVERITY_CHANGED':
      summary = formatSeverityChanged(payload);
      break;
    case 'SLA_BREACHED':
      summary = formatSlaBreached(payload);
      break;
    case 'NOTE_ADDED':
      // 서버 addNote 는 payload 를 { body } 로 저장한다. `note` 는 예전 키로,
      // 이미 쌓인 행이 있어 fallback 으로 남긴다(둘 다 없으면 빈 요약).
      summary = truncate(payload.body ?? payload.note);
      break;
    default:
      // Forward-compat: unknown types render label = raw type +
      // empty summary. The timeline still shows them; ops just
      // can't read the payload until the windows build catches up.
      summary = '';
      break;
  }
  return { label, summary };
}
